import React, { createContext, useContext, useEffect, useState } from 'react';
import { isAdminHost } from '../admin-panel/config/adminHost';
import {
  cacheComingSoonEnabled,
  cachedComingSoonEnabled,
  grantComingSoonAccess,
  hasComingSoonAccess,
} from './comingSoonAccess';
import { getPublicComingSoonStatus, verifyComingSoonAccessCode } from '../api/system/systemStatus';

const ComingSoonContext = createContext(null);
const POLL_MS = 12000;

export function useComingSoon() {
  const context = useContext(ComingSoonContext);
  if (!context) {
    throw new Error('useComingSoon must be used within a ComingSoonProvider');
  }
  return context;
}

export function ComingSoonProvider({ children }) {
  const adminHost = isAdminHost();
  const [enabled, setEnabled] = useState(() => !adminHost && cachedComingSoonEnabled() === true);
  const [unlocked, setUnlocked] = useState(hasComingSoonAccess);
  const [loading, setLoading] = useState(() => !adminHost && cachedComingSoonEnabled() === null);

  useEffect(() => {
    if (adminHost) return undefined;

    let cancelled = false;

    const load = async () => {
      try {
        const status = await getPublicComingSoonStatus();
        if (cancelled) return;
        const next = Boolean(status?.enabled);
        setEnabled(next);
        cacheComingSoonEnabled(next);
        if (!cancelled) setLoading(false);
      } catch (error) {
        console.error('Error loading coming soon status:', error);
        if (cancelled) return;
        if (cachedComingSoonEnabled() === false) {
          setEnabled(false);
          setLoading(false);
          return;
        }
        setEnabled(true);
        setLoading(false);
      }
    };

    load();
    const interval = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [adminHost]);

  const unlockWithCode = async (code) => {
    await verifyComingSoonAccessCode(code);
    grantComingSoonAccess();
    setUnlocked(true);
    return true;
  };

  const unlockPreview = () => {
    grantComingSoonAccess();
    setUnlocked(true);
  };

  return (
    <ComingSoonContext.Provider
      value={{
        enabled,
        unlocked,
        loading,
        unlockWithCode,
        unlockPreview,
      }}
    >
      {children}
    </ComingSoonContext.Provider>
  );
}
